/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, HostBinding, ElementRef, Renderer, ViewEncapsulation } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { Align } from './align';
import { getPopupClassNameFromAlign, getAlignFromPlacement } from './utils';

import classNames from 'classnames';


export type PopupPlacement =
    'top' | 'left' | 'right' | 'bottom' |
    'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' |
    'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';
/**
 * Popup
 * 参考 https://github.com/react-component/trigger
 */
export class Popup extends Align implements OnInit, OnDestroy {

     prefixCls: string;

    // @Input() align: object = {}; // Already define in super class Align

     destroyPopupOnHide: boolean;
/**
 * 用户CSS样式
 */
class: string;

     popupStyle: object;

     action: string | string[];

     animation: any;

     transitionName: string | object;
/**
 * 是否已遮罩
 */
mask: boolean;
/**
 * 遮罩动画名
 */
maskAnimation: string;
/**
 * 遮罩过渡效果
 */
maskTransitionName: string | object;

     zIndex: number;
/**
 * 气泡框位置，可选 top left right bottom topLeft topRight bottomLeft bottomRight leftTop leftBottom rightTop rightBottom。
 * 默认值为top。
 */
placement: PopupPlacement = 'top';

     builtinPlacements: Object;
private currentAlignClassName: string;
/**
 * 冒泡触发器：目标Element
 */
private _triggerElement: HTMLElement;
private _visible: boolean;
/**
 * @return {?}
 */
get visible(): boolean {
        return this._visible;
    }
/**
 * @param {?} visible
 * @return {?}
 */
set visible(visible: boolean) {
        this._visible = visible;
        this.disabled = !visible;
        this.currentAlignClassName = null;
    }
/**
 * 冒泡样式
 * @return {?}
 */
get classes(): string {
        const /** @type {?} */ align = this.getAlign();
        const /** @type {?} */ alignClass = this.currentAlignClassName || this.getClassNameFromAlign(align);
        const /** @type {?} */ classes = classNames(this.prefixCls, {
            [`${this.prefixCls}-hidden`]: !this.visible,
            [`${alignClass}`]: alignClass
        }, this.class);
        return classes;
    }

    onMouseEnterFn: (_: any) => void;
    onMouseLeaveFn: (_: any) => void;
/**
 * @param {?} renderer
 * @param {?} elementRef
 */
constructor(
private renderer: Renderer,
private elementRef: ElementRef) {
        super(renderer, elementRef);
        this.monitorWindowResize = true;
    }
/**
 * @return {?}
 */
ngOnInit(): void {
        super.ngOnInit();
    }
/**
 * @return {?}
 */
ngOnDestroy(): void {
        super.ngOnDestroy();
    }
/**
 * @param {?} align
 * @return {?}
 */
getClassNameFromAlign(align: any): any {
        const /** @type {?} */ classes = [];
        if (this.placement && this.builtinPlacements) {
            classes.push(getPopupClassNameFromAlign(this.builtinPlacements, this.prefixCls, align));
        }
        return classes.join(' ');
    }
/**
 * @return {?}
 */
getAlign(): {} {
        const /** @type {?} */ align = super.getAlign();
        if (this.placement && this.builtinPlacements) {
            return getAlignFromPlacement(this.builtinPlacements, this.placement, align);
        }
        return align;
    }
/**
 * @param {?} align
 * @return {?}
 */
onAlign(align: any): void {
        super.onAlign(align);
        this.currentAlignClassName = this.getClassNameFromAlign(align);
    }
/**
 * @param {?} triggerElement
 * @return {?}
 */
registerTriggerElement(triggerElement: HTMLElement): void {
        this._triggerElement = triggerElement;
    }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnMouseEnter(fn: ((_: any) => void)): void {
        this.onMouseEnterFn = fn;
    }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnMouseLeave(fn: ((_: any) => void)): void {
        this.onMouseLeaveFn = fn;
    }
/**
 * <ant-popup> element: The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this.elementRef.nativeElement as HTMLElement));
    }
/**
 * @return {?}
 */
getBaseTarget(): any {
        return this._triggerElement;
    }
/**
 * @return {?}
 */
getMaskTransitionName() {
        let /** @type {?} */ transitionName = this.maskTransitionName;
        const /** @type {?} */ animation = this.maskAnimation;
        if (!transitionName && animation) {
            transitionName = `${this.prefixCls}-${animation}`;
        }
        return transitionName;
    }
/**
 * @return {?}
 */
getTransitionName() {
        let /** @type {?} */ transitionName = this.transitionName;
        if (!transitionName && this.animation) {
            transitionName = `${this.prefixCls}-${this.animation}`;
        }
        return transitionName;
    }
/**
 * @return {?}
 */
getMaskClassName(): any {
        return {
            [`${this.prefixCls}-mask`]: true,
            [`${this.prefixCls}-mask-hidden`]: !this.visible
        };
    }
/**
 * @return {?}
 */
getPopupClassName(): any {
        return `${this.prefixCls}-content`;
    }
/**
 * @return {?}
 */
getZIndexStyle(): any {
        return { 'z-index': this.zIndex };
    }
/**
 * @param {?} event
 * @return {?}
 */
onMouseEnter(event: Event): void {
        if (this.onMouseEnterFn) {
            this.onMouseEnterFn(event);
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onMouseLeave(event: Event): void {
        if (this.onMouseLeaveFn) {
            this.onMouseLeaveFn(event);
        }
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-popup',
    template: `
        <div #mask
           [ngStyle]="getZIndexStyle()"
           [ngClass]="getMaskClassName()"></div>
        <div #popup [style]="popupStyle"
           [ngStyle]="getZIndexStyle()"
           [ngClass]="getPopupClassName()"
           (mouseEnter)="onMouseEnter($event)" (mouseLeave)="onMouseLeave($event)"></div>
    `,
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'destroyPopupOnHide': [{ type: Input },],
'class': [{ type: Input },],
'popupStyle': [{ type: Input },],
'action': [{ type: Input },],
'animation': [{ type: Input },],
'transitionName': [{ type: Input },],
'mask': [{ type: Input },],
'maskAnimation': [{ type: Input },],
'maskTransitionName': [{ type: Input },],
'zIndex': [{ type: Input },],
'placement': [{ type: Input },],
'builtinPlacements': [{ type: Input },],
'visible': [{ type: Input },],
'classes': [{ type: HostBinding, args: ['class', ] },],
};
}

function Popup_tsickle_Closure_declarations() {
/** @type {?} */
Popup.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Popup.ctorParameters;
/** @type {?} */
Popup.propDecorators;
/** @type {?} */
Popup.prototype.prefixCls;
/** @type {?} */
Popup.prototype.destroyPopupOnHide;
/**
 * 用户CSS样式
 * @type {?}
 */
Popup.prototype.class;
/** @type {?} */
Popup.prototype.popupStyle;
/** @type {?} */
Popup.prototype.action;
/** @type {?} */
Popup.prototype.animation;
/** @type {?} */
Popup.prototype.transitionName;
/**
 * 是否已遮罩
 * @type {?}
 */
Popup.prototype.mask;
/**
 * 遮罩动画名
 * @type {?}
 */
Popup.prototype.maskAnimation;
/**
 * 遮罩过渡效果
 * @type {?}
 */
Popup.prototype.maskTransitionName;
/** @type {?} */
Popup.prototype.zIndex;
/**
 * 气泡框位置，可选 top left right bottom topLeft topRight bottomLeft bottomRight leftTop leftBottom rightTop rightBottom。
 * 默认值为top。
 * @type {?}
 */
Popup.prototype.placement;
/** @type {?} */
Popup.prototype.builtinPlacements;
/** @type {?} */
Popup.prototype.currentAlignClassName;
/**
 * 冒泡触发器：目标Element
 * @type {?}
 */
Popup.prototype._triggerElement;
/** @type {?} */
Popup.prototype._visible;
/** @type {?} */
Popup.prototype.onMouseEnterFn;
/** @type {?} */
Popup.prototype.onMouseLeaveFn;
/** @type {?} */
Popup.prototype.renderer;
/** @type {?} */
Popup.prototype.elementRef;
}



interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
